import * as Sentry from '@sentry/react';
import { HttpStatusCode } from 'axios';
import { ApiError } from './unwrapApiResponse';
import { NotAuthenticatedError } from './AxiosWrapper';

/**
 * Single decision point for whether a query/mutation error should reach
 * Sentry. Reporting happens in exactly one place (the QueryCache /
 * MutationCache onError hooks), so there is no cross-layer dedup to do here —
 * this only filters out routine, non-actionable failures:
 *
 * - NotAuthenticatedError: expected control flow during logout / auth
 *   transitions, not a defect.
 * - 401: routine access-token expiry, re-fired on every protected call while
 *   the session refreshes — pure noise.
 * - `expected` ApiErrors: business conflicts the product handles (e.g. a 409
 *   "already selected"), opted out at the throw site.
 *
 * Everything else is reported, including status-undefined (network / unknown)
 * failures and non-401 4xx such as the deleted-stack 403/404 that motivated
 * this work.
 */
export const shouldReportApiError = (error: unknown): boolean => {
  if (error instanceof NotAuthenticatedError) return false;
  if (error instanceof ApiError) {
    if (error.expected) return false;
    if (error.status === HttpStatusCode.Unauthorized) return false;
  }
  return true;
};

// Sentry drops tag values over 200 chars and indexes tags for search, so tags
// must be low-cardinality. The `route` tag is primarily the query/mutation
// key's leading string — a bounded, human-named set (e.g. 'transactions') that
// is identical across every failure mode of an endpoint (5xx, 4xx, network).
// When there is no string key (e.g. a keyless mutation) we fall back to a
// normalized ApiError.url: host stripped (so staging/prod/dev-proxy collapse)
// and id-like segments rewritten to ':id'. The raw url and full key — which
// carry entity ids and can exceed 200 chars — go in `extra` (unindexed).
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isIdSegment = (seg: string): boolean =>
  UUID_SEGMENT.test(seg) ||
  /^\d+$/.test(seg) ||
  // Opaque ids (nanoid/cuid/hex): alphanumeric, contains a digit, and long
  // enough not to be a descriptive word. Keeps hyphenated names like
  // 'report-packs' and 'reconciliation-adjustments-export' intact.
  (/^[a-z0-9]+$/i.test(seg) && /\d/.test(seg) && seg.length >= 8);

export const normalizeRoute = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  const path = url.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0];
  if (!path) return undefined;
  return (
    path
      .split('/')
      .map((seg) => (seg && isIdSegment(seg) ? ':id' : seg))
      .join('/') || undefined
  );
};

const routeTag = (error: unknown, fallbackKey: unknown): string => {
  if (typeof fallbackKey === 'string') return fallbackKey;
  const url = error instanceof ApiError ? error.url : undefined;
  return normalizeRoute(url) ?? 'unknown';
};

const statusTag = (error: unknown): string =>
  error instanceof ApiError && error.status !== undefined
    ? String(error.status)
    : 'unknown';

const apiUrl = (error: unknown): string | undefined =>
  error instanceof ApiError ? error.url : undefined;

// The response body's own message, unsanitized. ApiError.message is filtered
// for the customer's sake (Joi predicates, field names), which strips exactly
// the detail that makes an issue diagnosable — so the raw text rides along in
// `extra`, where only we read it.
const apiServerMessage = (error: unknown): string | undefined =>
  error instanceof ApiError ? error.serverMessage : undefined;

// Single capture path: filter, then tag route + status (bounded) with the raw
// url and any caller-supplied context in `extra` (unindexed).
const report = (
  error: unknown,
  route: string,
  extra: Record<string, unknown>
) => {
  if (!shouldReportApiError(error)) return;
  Sentry.captureException(error, {
    tags: { route, api_status: statusTag(error) },
    extra: {
      ...extra,
      api_url: apiUrl(error),
      api_server_message: apiServerMessage(error),
    },
  });
};

/**
 * Report a React Query queryFn error to Sentry. Tags carry only the route
 * (queryKey[0]) and status (bounded cardinality); the full query key and raw
 * url go in `extra`.
 */
export const reportQueryError = (
  error: unknown,
  queryKey: readonly unknown[]
) =>
  report(error, routeTag(error, queryKey[0]), {
    query_key: JSON.stringify(queryKey),
  });

/** Report a React Query mutationFn error to Sentry (see reportQueryError). */
export const reportMutationError = (
  error: unknown,
  mutationKey: readonly unknown[] | undefined
) =>
  report(error, routeTag(error, mutationKey?.[0]), {
    mutation_key: JSON.stringify(mutationKey ?? 'unknown'),
  });

/**
 * Report a failure from an imperative (non-React-Query) call site — where the
 * QueryCache/MutationCache onError hooks don't apply. Same filtering and status
 * tagging as the query/mutation reporters; the caller supplies a stable `route`
 * name. This is the opt-in, per-site capture for direct callProtectedApi/callApi
 * consumers (most such sites already `console.error` + toast on failure, but had
 * no Sentry signal).
 */
export const reportApiError = (error: unknown, route: string) =>
  report(error, route, {});
